import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Import actions - Ensure this path is correct
import {
    createVideoSetting,
    updateVideoSetting,
    getAllVideoSettings,
} from '../../actions/videoSettingsAction'; 
import { Link } from 'react-router-dom'; 
// MessageModal Component - Ensure this path is correct
import MessageModal from '../Pages/MessageModel'; 

// ==================== UTILITY FUNCTIONS ====================
/**
 * Retrieves the hospital ID from localStorage.
 * @returns {string} The hospital ID or '0'.
 */
const getHospitalId = () => {
    try {
        const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
        // Ensure it's a string for consistency
        return String(loginData.hospitalID || '0'); 
    } catch (e) {
        console.error("Error parsing loginData from localStorage:", e);
        return '0';
    }
};

const VideoSettings = () => {
    const dispatch = useDispatch();
    
    // Stable hospital ID derived from utility function
    const currentHospitalId = useMemo(() => getHospitalId(), []); 

    // **1. Redux State & Selectors**
    // Get state from Redux store, using the structure defined in videoSettingReducer.js
    const {
        videoSettings = [], 
        videoSettingLoading,
        videoSettingError,
    } = useSelector(state => state.videoSetting || {}); 

    // **2. Local Component States**
    const [videoServiceSettings, setVideoServiceSettings] = useState({
        hospitalID: currentHospitalId, // For submission data
        account_sid: "",
        api_key: "",
        api_secret: "",
        auth_token: "",
        status: "0", // Use "0" for Disabled, "1" for Enabled (string format)
    });

    // Message Modal states
    const [modalShow, setModalShow] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState("");
    const [modalVariant, setModalVariant] = useState("info");
    const [modalLoading, setModalLoading] = useState(false);

    // --- Message Modal Functions (using useCallback) ---
    const showMessage = useCallback((title, message, variant = "info") => {
        setModalTitle(title);
        setModalMessage(message);
        setModalVariant(variant);
        setModalShow(true);
    }, []);

    const showSuccess = useCallback((message) => {
        showMessage("Success", message, "success");
    }, [showMessage]);

    const showError = useCallback((message) => {
        showMessage("Error", message, "danger"); 
    }, [showMessage]);
    
    const handleModalClose = useCallback(() => { 
        setModalShow(false); 
        setModalLoading(false);
    }, []);
    // --- End of Modal Functions ---

    // **3. Filtering & Derived State**
    // Filter settings for the current hospital ID
    const hospitalVideoSettings = useMemo(() => {
        return (videoSettings || []).filter(setting =>
            // Check against both 'hospitalID' and 'hid' for robust data access
            String(setting.hospitalID || setting.hid) === currentHospitalId
        );
    }, [videoSettings, currentHospitalId]);

    // Check if settings already exist for the current hospital
    const hasVideoSettings = useMemo(() => hospitalVideoSettings.length > 0, [hospitalVideoSettings]);

    // **4. Data Loading Logic**
    const loadVideoSettings = useCallback(() => {
        // The action will retrieve the hospital ID internally
        dispatch(getAllVideoSettings(currentHospitalId)); 
    }, [dispatch, currentHospitalId]);

   // **5. Initial Data Load Effect**
   useEffect(() => {
        loadVideoSettings();
        // No cleanup dispatch(resetPaymentState) equivalent needed unless explicitly defined
    }, [loadVideoSettings]); 

    // **6. Populate form when Redux state changes (Auto-populate logic)**
    useEffect(() => {
        if (hospitalVideoSettings.length > 0) {
            const firstSetting = hospitalVideoSettings[0];
            
            const { 
                account_sid, 
                api_key, 
                api_secret, 
                auth_token, 
                status 
            } = firstSetting;

            // Map incoming API status (e.g., 'active'/'inactive' or '1'/'0') to '1'/'0' string for form
            const normalizedStatus = String(status) === 'active' || String(status) === '1' ? '1' : '0';

            setVideoServiceSettings(prev => ({ 
                ...prev, 
                account_sid: account_sid || "",
                api_key: api_key || "",
                api_secret: api_secret || "",
                auth_token: auth_token || "",
                status: normalizedStatus,
                hospitalID: String(firstSetting.hospitalID || firstSetting.hid || currentHospitalId)
            }));
        } else if (!videoSettingLoading) {
             // Reset form for CREATE state if loading is complete and no settings found
             setVideoServiceSettings({
                hospitalID: currentHospitalId,
                account_sid: "",
                api_key: "",
                api_secret: "",
                auth_token: "",
                status: "0",
            });
        }
    }, [hospitalVideoSettings, currentHospitalId, videoSettingLoading]);

    // **7. Handler for Form Input Change**
    const handleVideoChange = useCallback((field, value) => {
        setVideoServiceSettings(prev => ({ ...prev, [field]: value }));
    }, []);

    // **8. Submission Handler (Create or Update)**
    const handleVideoSettingSubmit = async (e) => {
        e.preventDefault();
        setModalLoading(true);

        const dataToSubmit = {
            ...videoServiceSettings,
            hospitalID: currentHospitalId,
            // Map form status '1'/'0' to API expected value (e.g., 'active'/'inactive' if API is inconsistent)
            // Assuming API backend expects 'active' or 'inactive' based on common patterns:
            status: videoServiceSettings.status === '1' ? 'active' : 'inactive', 
        };
        
        try {
            if (hasVideoSettings && hospitalVideoSettings[0].id) {
                // UPDATE existing setting
                await dispatch(updateVideoSetting({
                    ...dataToSubmit,
                    id: hospitalVideoSettings[0].id 
                }));
                showSuccess('Video settings updated successfully!');
            } else {
                // CREATE new setting
                await dispatch(createVideoSetting(dataToSubmit));
                showSuccess('Video settings created successfully!');
            }
            
            loadVideoSettings(); // Refresh data after successful save/update
            
        } catch (error) {
            // Error handling relies on the action creator rejecting the promise
            showError(`Failed to save video settings: ${error.message}`);
        } finally {
            setModalLoading(false);
        }
    };

   // **9. Render Function**
   const renderVideoSettingsTab = () => (
    <div className="tab-pane fade show active">
        <div className="row">
            <div className="col-12">
                <div className="card mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fas fa-video me-2 text-primary"></i>
                            Video Conference Service Configuration
                        </h5>
                       
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleVideoSettingSubmit}>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <i className="fas fa-id-badge me-1 text-info"></i>
                                            Account SID *
                                        </label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={videoServiceSettings.account_sid}
                                            onChange={(e) => handleVideoChange('account_sid', e.target.value)} 
                                            required 
                                            disabled={videoSettingLoading || modalLoading}
                                            placeholder="Enter Account SID"
                                        />
                                        <div className="form-text">Your Video Service Account SID</div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <i className="fas fa-key me-1 text-warning"></i>
                                            API Key *
                                        </label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={videoServiceSettings.api_key}
                                            onChange={(e) => handleVideoChange('api_key', e.target.value)} 
                                            required 
                                            disabled={videoSettingLoading || modalLoading}
                                            placeholder="Enter API Key"
                                        />
                                        <div className="form-text">Your API Key for video access</div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <i className="fas fa-lock me-1 text-danger"></i>
                                            API Secret *
                                        </label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={videoServiceSettings.api_secret}
                                            onChange={(e) => handleVideoChange('api_secret', e.target.value)} 
                                            required 
                                            disabled={videoSettingLoading || modalLoading}
                                            placeholder="Enter API Secret"
                                        />
                                        <div className="form-text">Your API Secret</div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <i className="fas fa-shield-alt me-1 text-success"></i>
                                            Auth Token *
                                        </label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={videoServiceSettings.auth_token}
                                            onChange={(e) => handleVideoChange('auth_token', e.target.value)} 
                                            required 
                                            disabled={videoSettingLoading || modalLoading}
                                            placeholder="Enter Auth Token"
                                        />
                                        <div className="form-text">Your video service authentication token</div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">Status</label>
                                        <select 
                                            className="form-select" 
                                            value={videoServiceSettings.status}
                                            onChange={(e) => handleVideoChange('status', e.target.value)}
                                            disabled={videoSettingLoading || modalLoading}
                                        >
                                            <option value="0">Disabled</option>
                                            <option value="1">Enabled</option>
                                        </select>
                                        <div className="form-text">Enable or disable video conferencing</div>
                                    </div>
                                </div>
                            </div>
                            
                            {videoSettingError && (
                                <div className="alert alert-danger">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    {videoSettingError}
                                </div>
                            )}

                            <div className="d-flex gap-2 justify-content-end border-top pt-3">
                                <button 
                                    type="submit" 
                                    className="save-button" // Ensure this class is defined in your CSS
                                    disabled={videoSettingLoading || modalLoading} 
                                >
                                    {videoSettingLoading || modalLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save me-2"></i>
                                            {hasVideoSettings ? 'Update Video Settings' : 'Save Video Settings'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// **10. Main Render**
            
return (
    <div className="page-wrapper ml-0">
        <div className="content-container">
            <div className="page-header filter-bar rounded">
                <div className="row align-items-center">
                    <div className="col">
                        <h4 className="page-title">Video Conference Settings</h4>
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/doctor/dashboard">Admin Dashboard</Link>
                            </li>
                            <li className="breadcrumb-item active">
                                Configure and manage your video conference
                            </li>
                        </ul>
                        <div className="mt-2">
                            <small className="opacity-75">Hospital ID: {currentHospitalId}</small>
                        </div>
                    </div>
                </div>
            </div>

            <div className='card'>
                <div className="card-body pt-3">
                    {renderVideoSettingsTab()}
                </div>
            </div>
        </div>

        {/* Message Modal - This comment works INSIDE return */}
        <MessageModal
            show={modalShow}
            onClose={handleModalClose}
            title={modalTitle}
            message={modalMessage}
            variant={modalVariant}
            showConfirm={false}
            cancelText="Close"
            loading={modalLoading}
        />
    </div>
);


};

export default VideoSettings;